import React, { useEffect, useState } from 'react';
import { memberIdentificationClient } from 'tradera-utils/http';
import './document-scanning.scss';
import { Trans, useTranslator } from 'tradera-lang/translate';
import { LocalLoader } from 'tradera-components/loader/loader';
import qs from 'qs';

const STATE = {
    VERIFYING: 'VERIFYING',
    ERROR_UNAVAILABLE: 'ERROR_UNAVAILABLE',
    CONTACT_CUSTOMER_SUPPORT: 'CONTACT_CUSTOMER_SUPPORT'
};

export const handleMitIdQueryParam = (t, publishAction, errorAction) => {
    const { identified } = qs.parse(location.search, {
        ignoreQueryPrefix: true
    });
    if (identified === '1') {
        memberIdentificationClient
            .command('MitIdSessionStatus', {})
            .then(({ errors }) => {
                if (errors.length > 0) {
                    errorAction(
                        t(
                            'international_identification_contactCustomerSupportText'
                        )
                    );
                } else {
                    publishAction(t);
                }
                return;
            })
            .catch(() => {
                errorAction(t('international_identification_errorUnavailable'));
            });
    }

    if (identified === '0') {
        errorAction(
            t('international_identification_contactCustomerSupportText')
        );
    }
};

export const MitId = ({ onVerifyCancel }) => {
    useEffect(() => {
        memberIdentificationClient
            .command('MitIdCreateSession', { purpose: 'SYI', isMobile: true })
            .then(({ redirectUrl }) => (window.location = redirectUrl))
            .catch(onVerifyCancel);
    }, [onVerifyCancel]);
    return null;
};

export const MitIdDesktop = ({ onVerifyComplete, onVerifyCancel }) => {
    const { t } = useTranslator();
    const [isLoading, setIsLoading] = useState(true);

    const [verifyingState, setVerifyingState] = useState(STATE.VERIFYING);

    useEffect(() => {
        if (verifyingState !== STATE.VERIFYING) {
            return;
        }

        let currentTimeoutId = null;
        const poll = mitIdWindow =>
            new Promise((resolve, reject) => {
                const call = () => {
                    currentTimeoutId = setTimeout(() => {
                        if (mitIdWindow.closed) {
                            return reject({ userClosedWindow: true });
                        }
                        try {
                            // Result will appear when verification is completed.
                            memberIdentificationClient
                                .command('MitIdSessionStatus', {})
                                .then(result => {
                                    if (result && result.isCompleted) {
                                        mitIdWindow.close();
                                        return resolve(result);
                                    }
                                    return;
                                })
                                .catch(() => {
                                    mitIdWindow.close();
                                    reject();
                                });
                        } catch (error) {
                            console.error(error);
                        } finally {
                            if (currentTimeoutId) {
                                call();
                            }
                        }
                    }, 2000);
                };
                call();
            });
        setIsLoading(true);
        memberIdentificationClient
            .command('MitIdCreateSession', { purpose: 'SYI', isMobile: false })
            .then(({ redirectUrl }) => {
                const height = 645;
                const width = 450;
                const windowHeight = window.innerHeight;
                const windowWidth = window.innerWidth;
                const top = (window.screenY || 0) + (windowHeight - height) / 2;
                const left = (window.screenX || 0) + (windowWidth - width) / 2;
                const mitIdWindow = window.open(
                    redirectUrl,
                    '_blank',
                    `fullscreen=no,height=${height},width=${width},menubar=no,left=${left},top=${top}`
                );
                return poll(mitIdWindow);
            })
            .then(({ errors }) => {
                setIsLoading(false);

                if (errors.length > 0) {
                    setVerifyingState(STATE.CONTACT_CUSTOMER_SUPPORT);
                } else {
                    onVerifyComplete();
                }
                return;
            })
            .catch(error => {
                setIsLoading(false);
                if (error && error.userClosedWindow) {
                    onVerifyCancel();
                } else {
                    setVerifyingState(STATE.ERROR_UNAVAILABLE);
                }
            });
        return () => {
            currentTimeoutId && clearTimeout(currentTimeoutId);
            currentTimeoutId = null;
        };
    }, [
        verifyingState,
        setVerifyingState,
        setIsLoading,
        onVerifyComplete,
        onVerifyCancel
    ]);

    return (
        <div className="document-scanning w-100 h-100 position-relative d-flex justify-content-center">
            {isLoading && <LocalLoader />}
            {verifyingState === STATE.ERROR_UNAVAILABLE && (
                <span className="text-danger p-5">
                    {t('international_identification_errorUnavailable')}
                </span>
            )}
            {verifyingState === STATE.CONTACT_CUSTOMER_SUPPORT && (
                <div className="text-center p-5">
                    <h4 className="my-4">
                        {t(
                            'international_identification_contactCustomerSupportHeading'
                        )}
                    </h4>
                    <p className="mb-4 text-left px-4">
                        <Trans
                            i18nKey="international_identification_contactCustomerSupportText_Trans"
                            components={{ a: <a /> }}
                        />
                    </p>
                    <button
                        className="btn btn-primary"
                        onClick={() => onVerifyCancel()}>
                        {t('international_identification_return')}
                    </button>
                </div>
            )}
        </div>
    );
};
